import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { UserSettings, DEFAULT_SETTINGS, SUPPORTED_LANGUAGES, SUPPORTED_TIMEZONES } from '../types/settings';
import { message } from 'antd';

interface SettingsContextType {
  settings: UserSettings;
  updateSettings: (newSettings: Partial<UserSettings>) => Promise<void>;
  resetSettings: () => Promise<void>;
  isLoading: boolean;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<UserSettings>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(false);

  // 从localStorage加载设置
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const stored = localStorage.getItem('user-settings');
        if (stored) {
          const parsedSettings = JSON.parse(stored);

          // 验证设置的有效性
          const validatedSettings: UserSettings = {
            language: SUPPORTED_LANGUAGES.some(lang => lang.code === parsedSettings.language)
              ? parsedSettings.language
              : DEFAULT_SETTINGS.language,
            timezone: SUPPORTED_TIMEZONES.some(tz => tz.code === parsedSettings.timezone)
              ? parsedSettings.timezone
              : DEFAULT_SETTINGS.timezone,
            theme: ['light', 'dark', 'auto'].includes(parsedSettings.theme)
              ? parsedSettings.theme
              : DEFAULT_SETTINGS.theme,
          };

          setSettings(validatedSettings);
        }
      } catch (error) {
        console.error('Failed to load settings from localStorage:', error);
        setSettings(DEFAULT_SETTINGS);
      }
    };

    loadSettings();
  }, []);

  // 保存设置到localStorage
  const saveSettings = async (newSettings: UserSettings): Promise<void> => {
    try {
      localStorage.setItem('user-settings', JSON.stringify(newSettings));

      // 保存语言到单独的key（为了向后兼容）
      localStorage.setItem('user-language', newSettings.language);

      // 保存时区到单独的key
      localStorage.setItem('user-timezone', newSettings.timezone);

      setSettings(newSettings);

      // 触发自定义事件
      window.dispatchEvent(new CustomEvent('settingsChanged', {
        detail: newSettings
      }));

      message.success('设置已保存');
    } catch (error) {
      console.error('Failed to save settings:', error);
      throw new Error('保存设置失败');
    }
  };

  // 更新设置
  const updateSettings = async (newSettings: Partial<UserSettings>): Promise<void> => {
    setIsLoading(true);
    try {
      const updatedSettings = { ...settings, ...newSettings };
      await saveSettings(updatedSettings);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '更新设置失败');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // 重置设置
  const resetSettings = async (): Promise<void> => {
    setIsLoading(true);
    try {
      await saveSettings(DEFAULT_SETTINGS);
      message.success('设置已重置');
    } catch (error) {
      message.error('重置设置失败');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const contextValue: SettingsContextType = {
    settings,
    updateSettings,
    resetSettings,
    isLoading,
  };

  return (
    <SettingsContext.Provider value={contextValue}>
      {children}
    </SettingsContext.Provider>
  );
};

// 使用设置的Hook
export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

// 便捷的设置访问Hook
export const useLanguage = () => {
  const { settings } = useSettings();
  return settings.language;
};

export const useTimezone = () => {
  const { settings } = useSettings();
  return settings.timezone;
};
